import { useCallback, useEffect, useRef, useState } from 'react'

export function useSnapCarousel(count: number) {
  const trackRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const [active, setActive] = useState(0)

  const goTo = useCallback(
    (index: number, instant = false) => {
      const track = trackRef.current
      const slide = slideRefs.current[index]
      if (!track || !slide) return
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      track.scrollTo({
        left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
        behavior: instant || reduce ? 'auto' : 'smooth',
      })
    },
    [],
  )

  const next = useCallback(() => goTo(Math.min(active + 1, count - 1)), [active, count, goTo])
  const prev = useCallback(() => goTo(Math.max(active - 1, 0)), [active, goTo])

  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    let frame = 0
    const measure = () => {
      const center = track.scrollLeft + track.clientWidth / 2
      let closest = 0
      let best = Infinity
      slideRefs.current.forEach((slide, i) => {
        if (!slide) return
        const d = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - center)
        if (d < best) {
          best = d
          closest = i
        }
      })
      setActive(closest)
    }
    const onScroll = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    track.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    // On desktop, open on the second page so three full pages are in view.
    if (window.matchMedia('(min-width: 1024px)').matches && count > 2) {
      goTo(1, true)
    }
    measure()
    return () => {
      cancelAnimationFrame(frame)
      track.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [count, goTo])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        next()
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        prev()
      }
    },
    [next, prev],
  )

  return { trackRef, slideRefs, active, goTo, next, prev, onKeyDown }
}
